import _ from 'lodash'
import * as THREE from 'three'
import { v4 as uuidv4 } from 'uuid'

import { EditorUserError } from '@glass-designer/utils/editorErrors'
import BaseTool from '@glass-designer/tools/baseTool'
import DragShape from '@glass-designer/objects/dragShape'
import GlassShape from '@glass-designer/model/glassShape'
import RectangleShape from '@glass-designer/model/shapes/rectangleShape'
import CircleShape from '@glass-designer/model/shapes/circleShape'
import SemiCircleShape from '@glass-designer/model/shapes/semiCircleShape'
import OvalShape from '@glass-designer/model/shapes/ovalShape'
import RectangleHole from '@glass-designer/model/holes/rectangleHole'
import CircleHole from '@glass-designer/model/holes/circleHole'
import OvalHole from '@glass-designer/model/holes/ovalHole'
import GlassSegment from '@/model/glassSegment'
import GlassShapeGroup from '@glass-designer/model/glassShapeGroup'
import AssemblyGroup from '@glass-designer/model/assemblyGroup'

import GlassDesignerTypes from '@glass-designer/types'
import MathsUtils from '@/utils/mathsUtils'
import EditorDatumUtils from '@glass-designer/utils/editorDatumUtils'

import GlassItemDialog from '@glass-designer/views/glassItemDialog'
import EditorAssemblyItemDialog from '@glass-designer/views/editorAssemblyItemDialog'
import { InventoryStockTypes, UOMTypes } from '@/store/types'



/**
 * Add Glass Shape Tool
 * Tool to create a new glass shape(s) object into the editor
 * Performed via dragging the shape / kit from the library into the scene
 */
export default class AddGlassShapeTool extends BaseTool {
  constructor(editor) {
    super(editor)

    this.type = GlassDesignerTypes.ToolTypes.addGlassShape

    this.draggingShapeActive = false
    this.shapeInfoList = null
    this.shapeBounds = null
    this.libraryItem = {}

    this.shapeDragObject = new DragShape(editor)
    this.shapeDragObject.setVisible(false)
    this.addChildObject(this.shapeDragObject)
  }

  // Tool Active
  // Called when the tool is active
  onToolActive() {
    super.onToolActive()

    // Reset
    this.draggingShapeActive = false
    this.shapeInfoList = []
    this.shapeDragObject.setVisible(false)
  }

  // Tool Inactive
  // Called when the tool is no longer active
  onToolInactive() {
    super.onToolInactive()

    this.shapeInfoList.forEach(shapeInfo => {
      this.editor.removeSceneObject(shapeInfo.shapeObject)
    })
    this.shapeInfoList = []

    this.draggingShapeActive = false
    this.shapeDragObject.setVisible(false)
  }

  // onMouseMoveEvent
  onMouseMoveEvent(ev, mouseData) {
    super.onMouseMoveEvent(ev, mouseData)

    if(this.draggingShapeActive) {
      var castRay = mouseData.raycaster

      if(this.editor.getOptionSnapGridEnabled()) {
        castRay = this.editor.calculateSnappedRaycast(mouseData.position)
      }

      // Create a plane for the cutout
      // Assumes the editing shape is sitting on the plane at origin (Z = 0)
      var editingPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0)
      var rayInt = new THREE.Vector3()
      if(castRay.ray.intersectPlane(editingPlane, rayInt)) {

        rayInt.x -= (this.shapeBounds.max.x - this.shapeBounds.min.x) / 2
        rayInt.y -= (this.shapeBounds.max.y - this.shapeBounds.min.y) / 2

        // Round to nearest mm
        rayInt.x = Math.round(rayInt.x)
        rayInt.y = Math.round(rayInt.y)

        this.shapeDragObject.setPosition(rayInt)
      }

      this.editor.flagRenderRequired()
    }
  }

  // onMouseUpEvent
  onMouseUpEvent(ev, mouseData) {
    super.onMouseUpEvent(ev, mouseData)

    if(this.draggingShapeActive) {
      var castRay = mouseData.raycaster

      if(this.editor.getOptionSnapGridEnabled()) {
        castRay = this.editor.calculateSnappedRaycast(mouseData.position)
      }

      // Create a plane for the cutout
      // Assumes the editing shape is sitting on the plane at origin (Z = 0)
      var editingPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0)
      var rayInt = new THREE.Vector3()
      if(castRay.ray.intersectPlane(editingPlane, rayInt)) {
        rayInt.x -= (this.shapeBounds.max.x - this.shapeBounds.min.x) / 2
        rayInt.y -= (this.shapeBounds.max.y - this.shapeBounds.min.y) / 2

        // Round to nearest mm
        rayInt.x = Math.round(rayInt.x)
        rayInt.y = Math.round(rayInt.y)

        this.shapeDragObject.setPosition(rayInt)
      }

      this._placeShape()

      this.editor.flagRenderRequired()
    }
  }


  /**
   * A library item was dragged into the editor while this tool was active
   */
  libraryItemDraggedToEditor(itemInfo) {
    this.draggingShapeActive = true
      
    this.shapeInfoList = []
    this.libraryItem = itemInfo
    let itemData = itemInfo.itemData || {}

    this.shapeBounds = new THREE.Box2()

    // Support multiple shapes
    itemData.shapeList.forEach(itemShapeInfo => {
      var shapeData = itemShapeInfo.shapeData
      if(!shapeData)
        return

      var outputShapeInfo = {
        shapeType: shapeData.shapeType,
        shapeData: shapeData.shapeData,
        shapeObject: null,
        segmentList: shapeData.segmentList,
        centerCutoutList: [],
        shapeGroupList: []
      }
      
      // Rectangle / Circle
      if(outputShapeInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle)
        outputShapeInfo.shapeObject = new RectangleShape(this.editor, null, shapeData)
      else if(outputShapeInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.circle)
        outputShapeInfo.shapeObject = new CircleShape(this.editor, null, shapeData)
      else if(outputShapeInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.semiCircle)
        outputShapeInfo.shapeObject = new SemiCircleShape(this.editor, null, shapeData)
      else if(outputShapeInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.oval)
        outputShapeInfo.shapeObject = new OvalShape(this.editor, null, shapeData)
      else if(outputShapeInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.custom) {
        
        // Custom shape
        outputShapeInfo.shapeObject = new GlassShape(this.editor, shapeData)
        outputShapeInfo.shapeObject.segmentList = (shapeData.segmentList || []).map(segmentInfo => {
          var newSeg = new GlassSegment(segmentInfo.type || GlassDesignerTypes.SegmentTypes.line, segmentInfo)
          newSeg.id = segmentInfo.id
          newSeg.isUserFixed = !!segmentInfo.isUserFixed

          return newSeg
        })
      }

      if(outputShapeInfo.shapeObject) {
        this.shapeBounds.union(outputShapeInfo.shapeObject.calculateSegmentBounds())

        // Add in center cutouts
        if(_.isArray(shapeData.centerCutoutList)) {
          shapeData.centerCutoutList.forEach(cutoutInfo => {
            // Remove certain fields to ensure they don't get processed & stored with the new objects
            delete cutoutInfo.cutoutData.id

            try {
              this._addCenterCutoutItemToShape(outputShapeInfo.shapeObject, cutoutInfo.cutoutData, cutoutInfo.offset)

              outputShapeInfo.centerCutoutList.push({
                cutoutData: cutoutInfo.cutoutData,
                offset: cutoutInfo.offset
              })
            } catch(err) {
              console.error(err)
            }
          })
        }

        // Add in groups
        if(_.isArray(shapeData.shapeGroupList)) {
          shapeData.shapeGroupList.forEach(shapeGroupInfo => {
            // Remove certain fields to ensure they don't get processed & stored with the new objects
            delete shapeGroupInfo.groupData.id

            outputShapeInfo.shapeGroupList.push({
              groupData: shapeGroupInfo.groupData
            })
          })
        }


        this.shapeInfoList.push(outputShapeInfo)

        // Hide
        outputShapeInfo.shapeObject.setVisible(false)
      }
    })
  
    this.shapeDragObject.buildFromGlassShape(this.shapeInfoList.map(info => info.shapeObject))
    this.shapeDragObject.setVisible(true)

    this.editor.flagRenderRequired()
  }


  /**
   * Release the shape & place a new shape into the editor
   */
  _placeShape() {
    this.draggingCutoutActive = false
    this.shapeDragObject.setVisible(false)

    // Clear the temporary shapes
    this.shapeInfoList.forEach(shapeInfo => {
      shapeInfo.shapeObject.dispose()
      this.editor.removeSceneObject(shapeInfo.shapeObject)
    })

    try {
      let libraryItem = this.libraryItem || {}
      var libraryType = libraryItem.type
      let itemData = libraryItem.itemData || {}
      var shapeData = {}
      let segmentMap = {}

      if(itemData.shapeList.length > 0)
        shapeData = itemData.shapeList[0].shapeData || {}
      
      var shapeType = GlassDesignerTypes.LibraryItemShapeTypes.custom
      if(this.shapeInfoList.length == 1)
        shapeType = this.shapeInfoList[0].shapeType

      // Cannot create a new panel for NCR orders
      if(this.editor.getOrder().isNCROrder()) {
        this.editor.component.errorMessage('Cannot create new items for NCR orders', 'Setup items for NCR in the order item list')

        this.shapeInfoList = []
        this.closeTool()
        return
      }

      if(libraryItem.type == GlassDesignerTypes.LibraryItemTypes.assembly) {
        this.displayCreateAssembly(libraryItem, shapeData, shapeType);

        // Clear out the details
        this.shapeInfoList = []
        this.closeTool()
        return
      }
      
      
      // Display dialog to enter in details (stock, price group, etc) for the new glass item
      this.editor.component.displayModal(GlassItemDialog, {
        isCreateMode: true,
        contactId: this.editor.getOrder().contactId,
        defaultSalesRateId: this.editor.getOrder().newCustomerDefaultSalesRateId,
        editorConfigData: this.editor.getEditorConfigurationData()
      }, {
        shapeType: shapeType,
        stockId: parseInt(shapeData.stockId || 0),
        stockPriceGroupId: parseInt(shapeData.stockPriceGroupId || 0),
        initialWidth: MathsUtils.roundNumber(parseFloat(shapeData.width || 100), 1),
        initialHeight: MathsUtils.roundNumber(parseFloat(shapeData.height || 100), 1),
        initialRadius: MathsUtils.roundNumber(parseFloat(shapeData.width || 500), 1)
      }, { mountBody: true })
      .then((itemData) => {
        if(itemData) {
          var newShape = null

          this.editor.startUndoGroup('New Glass Item')

          // Create the new glass shape with all the details
          this.shapeInfoList.forEach(shapeInfo => {

            if(shapeInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle) {
              newShape = new RectangleShape(this.editor, this.shapeDragObject.position, itemData)
            } else if(shapeInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.circle) {
              newShape = new CircleShape(this.editor, this.shapeDragObject.position, itemData)
            } else if(shapeInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.semiCircle) {
              newShape = new SemiCircleShape(this.editor, this.shapeDragObject.position, itemData)
            } else if(shapeInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.oval) {
              newShape = new OvalShape(this.editor, this.shapeDragObject.position, itemData)
            } else if(shapeInfo.shapeType == GlassDesignerTypes.LibraryItemShapeTypes.custom) {
              
              newShape = new GlassShape(this.editor, itemData)
              newShape.segmentList = (shapeInfo.segmentList || []).map(segmentInfo => {
                var newSeg = new GlassSegment(segmentInfo.type || GlassDesignerTypes.SegmentTypes.line, segmentInfo)
                newSeg.startPosition.set(segmentInfo.startPosition.x + this.shapeDragObject.position.x, segmentInfo.startPosition.y + this.shapeDragObject.position.y)
                newSeg.endPosition.set(segmentInfo.endPosition.x + this.shapeDragObject.position.x, segmentInfo.endPosition.y + this.shapeDragObject.position.y)
                newSeg.isUserFixed = !!segmentInfo.isUserFixed
                
                newSeg.arcCenterPosition.set(segmentInfo.arcCenterPosition.x + this.shapeDragObject.position.x, segmentInfo.arcCenterPosition.y + this.shapeDragObject.position.y)
                newSeg.arcUpdated()

                // Store mapping of segment IDs from stored data to use for datums / groups
                segmentMap[segmentInfo.id] = newSeg.id

                return newSeg
              })
            }

            if(newShape) {
              // Insert the center cutouts
              if(_.isArray(shapeInfo.centerCutoutList)) {
                shapeInfo.centerCutoutList.forEach(cutoutInfo => {
                  try {
                    this._addCenterCutoutItemToShape(newShape, cutoutInfo.cutoutData, cutoutInfo.offset, segmentMap)
                  } catch(err) {
                    console.error(err)
                  }
                })
              }

              // Apply groups
              if(_.isArray(shapeInfo.shapeGroupList)) {
                shapeInfo.shapeGroupList.forEach(groupInfo => {
                  try {
                    this._addGroupToShape(newShape, groupInfo.groupData, segmentMap)
                  } catch(err) {
                    console.error(err)
                  }
                })
              }

              newShape.segmentListUpdated()
              newShape.updateSceneObject()
              newShape.applyDefaultEdgework()
              newShape.applyDefaultPanelItems()
              newShape.updateDefaultStampItem()

              this.editor.onShapeUpdated(newShape)
              this.editor.addUndoShape(newShape, GlassDesignerTypes.UndoCommandTypes.add)
            }
          })

          this.editor.closeCurrentUndoGroup()
        }

        // Clear out the details
        this.shapeInfoList = []
        this.closeTool()
      })
    } catch(err) {

      // Display error
      if(err instanceof EditorUserError)
        this.editor.component.errorMessage('Error placing shape', err.message)

      console.error(err)
    }
  }


  /**
   * Display the create assembly process when dragging an assembly into the editor
   */
  displayCreateAssembly(libraryItem, shapeData, shapeType) {
    try {

      // If creating from a library item, we don't display the assembly dialog as it's too complex
      // We just create the assembly as-is
      if(!libraryItem.isCoreItem) {
        this.editor.startUndoGroup('New Assembly Item')

        var assemblyShapeList = []
        let assemblyMap = {}

        // Create all the shapes & restore from stock
        // Offset segment lists 
        this.shapeInfoList.forEach(shapeInfo => {
          if(shapeInfo.shapeType != GlassDesignerTypes.LibraryItemShapeTypes.custom)
            return

          let segmentMap = {}

          let newShape = new GlassShape(this.editor, {})
          
          // Deserialize from data first
          // Need to load stock info in
          newShape.deserialize(shapeInfo.shapeData)

          newShape.segmentList = (shapeInfo.segmentList || []).map(segmentInfo => {
            var newSeg = new GlassSegment(segmentInfo.type || GlassDesignerTypes.SegmentTypes.line, segmentInfo)
            newSeg.startPosition.set(segmentInfo.startPosition.x + this.shapeDragObject.position.x, segmentInfo.startPosition.y + this.shapeDragObject.position.y)
            newSeg.endPosition.set(segmentInfo.endPosition.x + this.shapeDragObject.position.x, segmentInfo.endPosition.y + this.shapeDragObject.position.y)
            newSeg.isUserFixed = !!segmentInfo.isUserFixed
            
            newSeg.arcCenterPosition.set(segmentInfo.arcCenterPosition.x + this.shapeDragObject.position.x, segmentInfo.arcCenterPosition.y + this.shapeDragObject.position.y)
            newSeg.arcUpdated()

            // Store mapping of segment IDs from stored data to use for datums / groups
            segmentMap[segmentInfo.id] = newSeg.id

            return newSeg
          })

          // Insert the center cutouts
          if(_.isArray(shapeInfo.centerCutoutList)) {
            shapeInfo.centerCutoutList.forEach(cutoutInfo => {
              try {
                this._addCenterCutoutItemToShape(newShape, cutoutInfo.cutoutData, cutoutInfo.offset, segmentMap)
              } catch(err) {
                console.error(err)
              }
            })
          }

          // Apply groups
          if(_.isArray(shapeInfo.shapeGroupList)) {
            shapeInfo.shapeGroupList.forEach(groupInfo => {
              try {
                this._addGroupToShape(newShape, groupInfo.groupData, segmentMap)
              } catch(err) {
                console.error(err)
              }
            })
          }

          newShape.segmentListUpdated()
          newShape.updateSceneObject()
          newShape.applyDefaultEdgework()
          newShape.applyDefaultPanelItems()
          newShape.updateDefaultStampItem()

          // Regenerate new assembly UUIDs
          newShape.assemblyLinkUUID = uuidv4()
          assemblyMap[shapeInfo.shapeData.assemblyLinkUUID] = newShape.assemblyLinkUUID

          this.editor.onShapeUpdated(newShape)
          this.editor.addUndoShape(newShape, GlassDesignerTypes.UndoCommandTypes.add)

          assemblyShapeList.push(newShape)
        })

        // Create assembly group & add all shapes
        // Restore assembly list in assembly
        // Create new assembly links
        let assemblyGroup = new AssemblyGroup(this.editor, {})
        assemblyGroup.addChildObjectList(assemblyShapeList)

        assemblyGroup.itemData = _.cloneDeep(libraryItem.itemData.assemblyData.itemData || {})

        // Remap the new assembly UUID
        if(assemblyGroup.itemData.assemblyOrderItemList) {
          assemblyGroup.itemData.assemblyOrderItemList.forEach(assemblyOrderItem => {
            let newAssemblyUUID = assemblyMap[assemblyOrderItem.assemblyLinkUUID]
            if(newAssemblyUUID) {
              assemblyOrderItem.assemblyLinkUUID = newAssemblyUUID
            }
          })
        }

        assemblyGroup.updateSceneObject()

        this.editor.onAssemblyUpdated(assemblyGroup)
        this.editor.addUndoShape(assemblyGroup, GlassDesignerTypes.UndoCommandTypes.add)

        this.editor.closeCurrentUndoGroup()
        return
      }


      // Display dialog to enter in details (stock, price group, etc) for the new glass item
      this.editor.component.displayModal(EditorAssemblyItemDialog, {
        isCreateMode: true,
        contactId: this.editor.getOrder().contactId,
        defaultSalesRateId: this.editor.getOrder().newCustomerDefaultSalesRateId,
        editorConfigData: this.editor.getEditorConfigurationData()
      }, {
        shapeType: shapeType,
        stockId: parseInt(shapeData.stockId || 0),
        stockPriceGroupId: parseInt(shapeData.stockPriceGroupId || 0),
        initialWidth: MathsUtils.roundNumber(parseFloat(shapeData.width || 100), 1),
        initialHeight: MathsUtils.roundNumber(parseFloat(shapeData.height || 100), 1),
        initialRadius: MathsUtils.roundNumber(parseFloat(shapeData.width || 500), 1)
      }, { mountBody: true })
      .then((itemData) => {
        if(itemData) {
         
          let assemblyShapeSpacing = 500

          this.editor.startUndoGroup('New Assembly Item')

          let shapePosition = new THREE.Vector2(this.shapeDragObject.position.x, this.shapeDragObject.position.y)

          var shapeList = []
          itemData.assemblyOrderItemList.forEach(assemblyOrderItem => {

            // Create shapes for glass items
            // Assumes only rectangle shapes for assemblies
            if(assemblyOrderItem.stockType == InventoryStockTypes.glass) {
              let newShape = new RectangleShape(this.editor, shapePosition, assemblyOrderItem)
              newShape.segmentListUpdated()
              newShape.updateSceneObject()
              newShape.applyDefaultEdgework()
              newShape.applyDefaultPanelItems()
              newShape.updateDefaultStampItem()

              this.editor.onShapeUpdated(newShape)
              this.editor.addUndoShape(newShape, GlassDesignerTypes.UndoCommandTypes.add)

              shapePosition.x += assemblyOrderItem.width + assemblyShapeSpacing

              // Link the assembly structure item to the shape
              newShape.assemblyLinkUUID = assemblyOrderItem.assemblyLinkUUID
              assemblyOrderItem.editorId = newShape.id    // Needed now?

              shapeList.push(newShape)

              // Check for edgework child items in the assembly              
              let childItems = itemData.assemblyOrderItemList.filter(childItem => childItem.parentOrderItemId == assemblyOrderItem.id)
              childItems.forEach(child => {
                // Only handle edgework items
                if(child.stockType != InventoryStockTypes.glassService)
                  return
                if(!child.stock || child.stock.uomType != UOMTypes.length)
                  return

                let assemblyEdgeworkFlags = []
                if(child.orderEntryConfigData && child.orderEntryConfigData.assemblyItemData)
                  assemblyEdgeworkFlags = child.orderEntryConfigData.assemblyItemData.edgeworkFlagList || []

                if(assemblyEdgeworkFlags.length == 0)
                  return

                // Must exist in the editor edgework list
                let edgework = this.editor.getEditorConfigurationData().edgeworkList.find(edgework => {
                  return edgework.orderEditorEdgeworkStocks.some(edgeStock => edgeStock.stockId == child.stock.id)
                })

                if(edgework) {

                  // Find shorts & longs for the rectangle that matches up with the edgework flags in the assembly
                  // This can only really work for new, unmodified rectangles
                  newShape.segmentList.forEach(segment => {
                    let edgeworkEdgeFlag = newShape._calculateEdgeworkFlag(segment.startPosition, segment.endPosition)
                    if(assemblyEdgeworkFlags.includes(edgeworkEdgeFlag)) {
                      newShape.updateSegmentEdgework(segment, edgework.id, 0)
                    }
                  })
                }
              })

              newShape.updateSceneObject()
            }
          })

          // Create the assembly group object
          let assemblyGroup = new AssemblyGroup(this.editor, itemData)
          assemblyGroup.addChildObjectList(shapeList)

          this.editor.onAssemblyUpdated(assemblyGroup)
          this.editor.addUndoShape(assemblyGroup, GlassDesignerTypes.UndoCommandTypes.add)

          this.editor.closeCurrentUndoGroup()
        }
      })

    } catch(err) {
      // Display error
      if(err instanceof EditorUserError)
        this.editor.component.errorMessage('Error placing assembly shape', err.message)

      console.error(err)
    }
  }


  /**
   * Add a center cutout from the library item data to a new shape
   * Center cutout data are stored with the library item
   */
  _addCenterCutoutItemToShape(shape, cutoutInfo, offsetInfo, segmentIdMap) {
    var holeObject = null

    // Cleanup the data to ensure IDs don't get set from stored data
    if(cutoutInfo.id)
      delete cutoutInfo.id
    let segments = cutoutInfo.segmentList || []
    segments.forEach(segmentData => {
      if(segmentData.id)
        delete segmentData.id
    })


    if(cutoutInfo.holeType == GlassDesignerTypes.LibraryItemShapeTypes.rectangle) {
      holeObject = new RectangleHole(this.editor, {
        width: parseInt(cutoutInfo.width || 100),
        height: parseInt(cutoutInfo.height || 50),
        cornerRadius: 5
      })
    }
    else if(cutoutInfo.holeType == GlassDesignerTypes.LibraryItemShapeTypes.circle) {
      holeObject = new CircleHole(this.editor, {
        radius: parseFloat(cutoutInfo.radius || 5)
      })
    }
    else if(cutoutInfo.holeType == GlassDesignerTypes.LibraryItemShapeTypes.oval) {
      holeObject = new OvalHole(this.editor, { width: parseInt(cutoutInfo.width || 5), height: parseInt(cutoutInfo.height || 5) })
    }

    if(holeObject) {
      holeObject.deserialize(cutoutInfo)

      // Set world point as offset from shape
      // Will be overridden by stored datum links if they are valid
      if(offsetInfo) {
        let shapeBounds = shape.calculateSegmentBounds()
        holeObject.worldPoint.set(shapeBounds.min.x + parseFloat(offsetInfo.x || 0), shapeBounds.min.y + parseFloat(offsetInfo.y || 0))

        EditorDatumUtils.calculateClosestDatumLinks(shape, holeObject.datumLinks, holeObject.worldPoint, { ignoreAllHoles: false, ignoreHoleId: holeObject.id })
        holeObject.setDatumLinks(holeObject.datumLinks)
      }

      // If datum links are specified, apply those using the segment map
      if(_.isArray(cutoutInfo.datumLinks)) {

        // Attempt to load & resolve the datum links
        let resultDatums = EditorDatumUtils.deserializeDatumLinks(shape, segmentIdMap, cutoutInfo.datumLinks)

        if(resultDatums.length >= 2) {
          holeObject.setDatumLinks(resultDatums)
        }
      }

      shape.addCenterCutout(holeObject)
    }
  }


  /**
   * Add a shape group from the library item data to a new shape
   * Group data is stored with the library item for shapes
   * Note: Segment map is the mapping of stored segment IDs to newly created segments
   */
  _addGroupToShape(shape, groupInfo, segmentIdMap) {
    let group = new GlassShapeGroup(this.editor)
    group.deserialize(groupInfo)

    // Map segments & update
    let updatedSegments = []
    if(groupInfo.segmentIdList) {
      groupInfo.segmentIdList.forEach(segmentId => {
        let srcSegment = shape.getSegmentById(segmentIdMap[segmentId])
        if(!srcSegment)
          return

        updatedSegments.push(srcSegment)
      })
    }

    // If not enough segments found, don't add the group
    if(updatedSegments.length != groupInfo.segmentIdList.length) {
      group.dispose()
      return null
    }

    shape.addChildObject(group)
    group.updateGroupSegments(updatedSegments)
    
    return group
  }
}